import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import SignupPromptModal from "@/components/SignupPromptModal";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import AboutPage from "@/pages/AboutPage";
import GalleryPage from "@/pages/GalleryPage";
import DonatePage from "@/pages/DonatePage";
import BookingPage from "@/pages/BookingPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import TestsPage from "@/pages/TestsPage";
import TestsExternPage from "@/pages/TestsExternPage";
import CoursesPage from "@/pages/CoursesPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import AdminPage from "@/pages/AdminPage";
import FreeLessonsPage from "@/pages/FreeLessonsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsConditionsPage from "@/pages/TermsConditionsPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import features from "@/config/features";
import { initSecurityMeasures, securityStyles } from "@/utils/security";

// Protected Route Component
function ProtectedRoute({ children, requireSubscription = false }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireSubscription && user.subscription_status !== "active" && user.role !== "admin") {
    return <Navigate to="/abonamente" replace />;
  }
  
  return children;
}

// Admin Route Component
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user || user.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function AppContent() {
  const location = useLocation();
  const { user } = useAuth();
  const isAdminPage = location.pathname.startsWith("/admin");
  const isDashboardPage = location.pathname.startsWith("/dashboard") || 
                          location.pathname.startsWith("/teste") ||
                          location.pathname.startsWith("/cursuri") ||
                          location.pathname.startsWith("/abonamente");
  const isAuthPage = location.pathname === "/login" || location.pathname === "/register";
  const isPublicPage = !isAdminPage && !isDashboardPage && !isAuthPage;

  return (
    <div className="App min-h-screen flex flex-col bg-slate-50">
      {!isAdminPage && !isDashboardPage && !isAuthPage && <Navbar />}
      <main className="flex-1">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/servicii" element={<ServicesPage />} />
          {features.aboutUs && <Route path="/despre" element={<AboutPage />} />}
          {features.gallery && <Route path="/galerie" element={<GalleryPage />} />}
          <Route path="/doneaza" element={<DonatePage />} />
          {features.bookings && <Route path="/programari" element={<BookingPage />} />}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/lectii-gratuite" element={<FreeLessonsPage />} />
          <Route path="/politica-confidentialitate" element={<PrivacyPolicyPage />} />
          <Route path="/termeni-conditii" element={<TermsConditionsPage />} />
          <Route path="/politica-cookies" element={<CookiePolicyPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/abonamente" element={
            <ProtectedRoute>
              <SubscriptionsPage />
            </ProtectedRoute>
          } />
          <Route path="/teste" element={
            <ProtectedRoute requireSubscription>
              <TestsPage />
            </ProtectedRoute>
          } />
          <Route path="/teste-drpciv" element={
            <ProtectedRoute>
              <TestsExternPage />
            </ProtectedRoute>
          } />
          {features.videoCourses && (
            <Route path="/cursuri" element={
              <ProtectedRoute requireSubscription>
                <CoursesPage />
              </ProtectedRoute>
            } />
          )}
          
          {/* Admin Routes */}
          <Route path="/admin/*" element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          } />
        </Routes>
      </main>
      {!isAdminPage && !isDashboardPage && !isAuthPage && <Footer />}
      <CookieBanner />
      {/* Signup prompt modal for non-authenticated users on public pages */}
      {!user && isPublicPage && <SignupPromptModal />}
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  // Initialize security measures and PWA on app load
  useEffect(() => {
    initSecurityMeasures();
    
    // Inject security styles
    const styleElement = document.createElement('style');
    styleElement.textContent = securityStyles;
    document.head.appendChild(styleElement);
    
    // Register Service Worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then((registration) => {
            console.log('PWA: Service Worker registered', registration.scope);
          })
          .catch((error) => {
            console.log('PWA: Service Worker registration failed', error);
          });
      });
    }
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <InstallAppPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}
export default App;
